/**
 * TECNILAND - AdminProtectedRoute Component
 * HOC para proteger rutas que requieren rol de administrador
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

// Verificar si un usuario es administrador (controlado por backend)
const isUserAdmin = (user) => {
  if (!user) return false;
  // Solo el backend decide quién es admin mediante isAdmin o role
  return user.isAdmin === true || user.role === 'admin';
};

/**
 * AdminProtectedRoute - Protege rutas que requieren usuario con rol admin
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componentes hijos a renderizar
 */
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Mientras verifica la autenticación, mostrar loading
  if (loading) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 70px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0e27'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          color: '#cccccc'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(0, 255, 0, 0.2)',
            borderTopColor: '#00ff00',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span>Verificando permisos...</span>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Si no está autenticado, redirigir al home
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Si está autenticado pero no es admin, mostrar acceso denegado
  if (!isUserAdmin(user)) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 70px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0a0e27 0%, rgba(45, 27, 78, 0.2) 50%, #0a0e27 100%)',
        padding: '2rem'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '500px',
          padding: '3rem',
          background: 'rgba(15, 18, 25, 0.9)',
          border: '2px solid rgba(255, 68, 68, 0.5)',
          borderRadius: '12px',
          boxShadow: '0 0 40px rgba(255, 68, 68, 0.2), 0 10px 40px rgba(0, 0, 0, 0.5)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 1.5rem',
            background: 'rgba(255, 68, 68, 0.1)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid rgba(255, 68, 68, 0.3)'
          }}>
            <svg 
              width="40" 
              height="40" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#ff4444" 
              strokeWidth="2"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2 style={{
            color: '#ff4444',
            fontSize: '1.75rem',
            marginBottom: '1rem',
            fontWeight: '600'
          }}>
            Acceso Denegado
          </h2>
          <p style={{
            color: '#cccccc',
            fontSize: '1rem',
            lineHeight: '1.6',
            marginBottom: '2rem'
          }}>
            No tienes permisos de administrador para acceder a esta sección. 
            Contacta con un administrador si crees que esto es un error.
          </p>
          <a 
            href="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.875rem 1.75rem',
              background: 'transparent',
              border: '2px solid #00ff00',
              borderRadius: '8px',
              color: '#00ff00',
              fontSize: '0.95rem',
              fontWeight: '600',
              textDecoration: 'none',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(0, 255, 0, 0.1)';
              e.target.style.boxShadow = '0 0 20px rgba(0, 255, 0, 0.3)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.boxShadow = 'none';
            }}
          >
            Volver al Inicio
          </a>
        </div>
      </div>
    );
  }

  // Si es admin, renderizar los hijos
  return children;
};

export default AdminProtectedRoute;
